import { useState } from "react";
import PractitionerCard from "./PractitionerCard";
import CrisisResourceCard from "./CrisisResourceCard";
import QuickFiltersBar from "./QuickFiltersBar";
import { Skeleton } from "@/components/ui/skeleton";
import { Practitioner } from "@/types/practitioner";
import { AlertCircle } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface PractitionerListProps {
  practitioners: Practitioner[];
  sortBy: string;
  onSortChange: (sortOption: string) => void;
  isLoading: boolean;
  error: string | null;
  setShowSidebar: (show: boolean) => void;
  hasMore: boolean;
  onLoadMore: () => void;
  filters?: {
    serviceCategories?: string[];
    distance?: string;
    date?: Date | undefined;
    timeSlot?: string;
  };
  onFiltersChange?: (filters: any) => void;
  timeSlots?: string[];
  isDateDisabled?: (date: Date) => boolean;
  userLocation?: {lat: number, lng: number} | null;
  locationError?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  isGettingLocation?: boolean;
  isLoadingAddress?: boolean;
  timezoneDisplay?: string | null;
  isApproximateLocation?: boolean;
  requestLocationAccess?: () => void;
}

const PractitionerList = ({
  practitioners,
  sortBy,
  onSortChange,
  isLoading,
  error,
  setShowSidebar,
  hasMore,
  onLoadMore,
  filters,
  onFiltersChange,
  timeSlots = [],
  isDateDisabled = () => false,
  userLocation = null,
  locationError = null,
  city = null,
  state = null,
  country = null,
  isGettingLocation = false,
  isLoadingAddress = false,
  timezoneDisplay = null,
  isApproximateLocation = false,
  requestLocationAccess = () => {},
}: PractitionerListProps) => {
  const { t } = useTranslation('practitioners');

  // Check if crisis intervention filter is active
  const isCrisisFilterActive = filters?.serviceCategories?.includes('crisis_intervention');

  // Show loading skeletons
  if (isLoading) {
    return (
      <div className="w-full">
        <div className="mb-4 flex justify-end">
          <div className="w-32 h-8 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="flex flex-col md:flex-row h-full">
                <div className="md:w-1/3 h-48 md:h-auto">
                  <Skeleton className="w-full h-full" />
                </div>
                <div className="md:w-2/3 p-6">
                  <Skeleton className="h-6 w-1/2 mb-2" />
                  <Skeleton className="h-4 w-1/3 mb-4" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Show error message
  if (error) {
    return (
      <div className="w-full">
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <h3 className="text-lg font-medium text-red-700 mb-1">{t('errorLoadingPractitioners')}</h3>
          <p className="text-red-600">{error}</p>
          <p className="mt-2 text-sm text-red-600">{t('refreshOrContact')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Quick Filters Bar */}
      {onFiltersChange && (
        <QuickFiltersBar
          distance={filters?.distance || 'any_distance'}
          onDistanceChange={(value) => onFiltersChange({ ...filters, distance: value })}
          date={filters?.date}
          onDateChange={(date) => onFiltersChange({ ...filters, date })}
          timeSlot={filters?.timeSlot || 'any_time'}
          onTimeSlotChange={(timeSlot) => onFiltersChange({ ...filters, timeSlot })}
          timeSlots={timeSlots}
          isDateDisabled={isDateDisabled}
          userLocation={userLocation}
          locationError={locationError}
          city={city}
          state={state}
          country={country}
          isGettingLocation={isGettingLocation}
          isLoadingAddress={isLoadingAddress}
          timezoneDisplay={timezoneDisplay}
          isApproximateLocation={isApproximateLocation}
          requestLocationAccess={requestLocationAccess}
        />
      )}

      <div className="mb-4 flex justify-between items-center">
        <div className="text-sm text-earth-600">
          {practitioners.length} {practitioners.length === 1 ? t('practitionerFound') : t('practitionersFound')}
        </div>
        <div className="flex items-center">
          <span className="text-sm text-earth-600 mr-2">{t('sortBy')}:</span>
          <Select value={sortBy} onValueChange={onSortChange}>
            <SelectTrigger className="w-[180px] border-amber-200">
              <SelectValue placeholder={t('highestRated')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rating">{t('highestRated')}</SelectItem>
              <SelectItem value="price">{t('priceLowest')}</SelectItem>
              <SelectItem value="price_high">{t('priceHighest')}</SelectItem>
              <SelectItem value="orders">{t('completedOrders')}</SelectItem>
              <SelectItem value="newest">{t('newest')}</SelectItem>
              <SelectItem value="distance">{t('nearest')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="space-y-6">
        {/* Show Crisis Resource Card at top if crisis filter is active */}
        {isCrisisFilterActive && <CrisisResourceCard />}
        
        {practitioners.length > 0 ? (
          practitioners.map(practitioner => (
                <PractitionerCard
                  key={practitioner.id}
                  practitioner={practitioner}
                  isActive={false}
                />
          ))
        ) : (
          <div className="text-center py-12 bg-amber-50 rounded-lg">
            <p className="text-lg text-earth-700">{t('noPractitionersMatch')}</p>
            <p className="text-earth-500">{t('tryAdjustingFilters')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PractitionerList;
